import {
  selectLocalEnglishVoice,
  waitForVoices,
  type Narrator,
  type NarratorCapabilities,
  type PlayOptions,
  type PlaybackHandle,
  type PrepareRequest,
  type PreparedNarration,
  type SpeechSynthesisAdapter,
  type SpeechUtteranceLike,
  type SpeechVoiceLike,
} from "./narrator.js";

export class NoLocalVoiceError extends Error {
  constructor() {
    super("no local English speech voice is available");
    this.name = "NoLocalVoiceError";
  }
}

/**
 * Web Speech fallback.
 *
 * It cannot route into the Web Audio graph, cannot be ducked, and — critically
 * — cannot be seeked. Starting mid-slot at the beginning of the entry would put
 * a Web Speech listener at a different point in the broadcast than a Kokoro
 * listener in the same slot, which breaks the wall-clock contract. The player
 * therefore stays silent for the rest of the current slot, speaks from the next
 * boundary, and cancels at the following one.
 */
export class WebSpeechNarrator implements Narrator {
  readonly id = "webspeech" as const;
  readonly capabilities: NarratorCapabilities = {
    graphRoutable: false,
    volumeControl: false,
    seekable: false,
    duckable: false,
  };

  private voice: SpeechVoiceLike | null = null;
  private disposed = false;

  constructor(
    private readonly synth: SpeechSynthesisAdapter,
    private readonly options: { voicesTimeoutMs?: number } = {},
  ) {}

  get selectedVoice(): SpeechVoiceLike | null {
    return this.voice;
  }

  async init(): Promise<void> {
    const voices = await waitForVoices(this.synth, this.options.voicesTimeoutMs);
    const voice = selectLocalEnglishVoice(voices);
    if (!voice) throw new NoLocalVoiceError();
    this.voice = voice;
  }

  prepare(request: PrepareRequest, signal: AbortSignal): Promise<PreparedNarration> {
    if (signal.aborted) {
      return Promise.reject(new DOMException("aborted", "AbortError"));
    }
    return Promise.resolve({
      slotIndex: request.slotIndex,
      entryId: request.entryId,
      text: request.text,
      durationSec: 0,
    });
  }

  play(prepared: PreparedNarration, options: PlayOptions): PlaybackHandle {
    let ended = false;
    const synth = this.synth;
    const utterance: SpeechUtteranceLike = {
      text: prepared.text,
      voice: this.voice,
      rate: 0.95,
      pitch: 0.95,
      volume: 1,
    };

    const finish = () => {
      if (ended) return;
      ended = true;
      options.onEnd();
    };

    utterance.onend = finish;
    utterance.onerror = finish;

    if (!this.disposed) synth.speak(utterance);

    return {
      get ended() {
        return ended;
      },
      startedAtSec: 0,
      offsetSec: 0,
      stop() {
        if (ended) return;
        ended = true;
        // Cancelling the utterance is what enforces "cancelled at the
        // following boundary"; without it the previous entry would talk over
        // the next slot.
        try {
          synth.cancel();
        } catch {
          // ignore
        }
      },
    };
  }

  cancel(): void {
    this.synth.cancel();
  }

  dispose(): void {
    this.disposed = true;
    this.synth.cancel();
  }
}

export function browserSpeechSynthesis(): SpeechSynthesisAdapter | null {
  if (typeof window === "undefined") return null;
  const synth = window.speechSynthesis;
  if (!synth) return null;
  return {
    getVoices: () => synth.getVoices().map(toVoiceLike),
    speak: (utterance) => {
      const real = new SpeechSynthesisUtterance(utterance.text);
      if (utterance.rate !== undefined) real.rate = utterance.rate;
      if (utterance.pitch !== undefined) real.pitch = utterance.pitch;
      if (utterance.volume !== undefined) real.volume = utterance.volume;
      const voice = utterance.voice;
      if (voice) {
        const match = synth.getVoices().find((candidate) => candidate.name === voice.name);
        if (match) real.voice = match;
      }
      real.onend = () => utterance.onend?.();
      real.onerror = (event) => utterance.onerror?.(event);
      synth.speak(real);
    },
    cancel: () => synth.cancel(),
    addEventListener: (type, listener) => synth.addEventListener(type, listener),
    removeEventListener: (type, listener) => synth.removeEventListener(type, listener),
  };
}

function toVoiceLike(voice: SpeechSynthesisVoice): SpeechVoiceLike {
  return {
    name: voice.name,
    lang: voice.lang,
    localService: voice.localService === true,
    default: voice.default,
  };
}
